/** One WebSocket for the whole app, with reconnect. Subscribers get every server message. */
import type { ClientMessage, ServerMessage } from '@gooflord/shared';
import { wsUrl } from './api';

type Listener = (m: ServerMessage) => void;
type StatusListener = (s: SocketStatus) => void;
export type SocketStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'expired';

class Socket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private queue: string[] = [];
  private attempts = 0;
  private timer: number | null = null;
  private wanted = false;
  status: SocketStatus = 'idle';

  connect() {
    this.wanted = true;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.setStatus('connecting');
    const ws = new WebSocket(wsUrl());
    this.ws = ws;
    ws.onopen = () => { this.attempts = 0; this.setStatus('open'); for (const m of this.queue.splice(0)) ws.send(m); };
    ws.onmessage = (e) => { let m: ServerMessage; try { m = JSON.parse(e.data as string); } catch { return; } for (const l of this.listeners) l(m); };
    ws.onclose = (e) => {
      if (this.ws !== ws) return; // replaced or deliberately disconnected
      this.ws = null;
      if (e.code === 4401) { this.wanted = false; this.queue = []; this.setStatus('expired'); return; } // session gone: the store signs out
      this.setStatus('closed');
      if (!this.wanted) return;
      const delay = Math.min(15000, 500 * 2 ** this.attempts++);
      this.timer = window.setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => { /* onclose follows */ };
  }
  disconnect() { this.wanted = false; this.queue = []; if (this.timer) clearTimeout(this.timer); const ws = this.ws; this.ws = null; ws?.close(); this.setStatus('idle'); }
  /** Sends now, or queues until the socket opens. Game actions are never queued: their seq would be stale by then. Returns whether it went out now. */
  send(m: ClientMessage): boolean {
    const s = JSON.stringify(m);
    if (this.ws?.readyState === WebSocket.OPEN) { this.ws.send(s); return true; }
    if (m.type !== 'act') this.queue.push(s);
    if (this.wanted) this.connect();
    return false;
  }
  on(l: Listener): () => void { this.listeners.add(l); return () => this.listeners.delete(l); }
  onStatus(l: StatusListener): () => void { this.statusListeners.add(l); l(this.status); return () => this.statusListeners.delete(l); }
  private setStatus(s: SocketStatus) { this.status = s; for (const l of this.statusListeners) l(s); }
}
export const socket = new Socket();
